import { useCallback, useEffect, useState } from "react";
import { ChannelsTab } from "../components/ChannelsTab";
import { apiDelete, apiPost, tryApiGet } from "../api/client";
import {
  MESSAGE_WINDOW_OPTIONS, MESSAGE_WINDOW_STORAGE_KEY, parseMessageWindow, sinceQuery, windowRows, type MessageWindow,
} from "../lib/messageTimeFilter";
import { useAppData } from "../state/AppData";
import { colorFor } from '../lib/definitions/palette';
import { isProtocol, PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { liveTransmitDisabledNotice, messageStateInfo, SIMULATED_SEND_NOTICE } from '../lib/definitions/messaging';
import { TRUST_TIER_INFO } from '../lib/definitions/trust';
import { clockTime, relativeTime } from "../lib/format";
import type {
  CreateSimulatedNodeRequest,
  MessageEndpoint,
  MessageRecord,
  PreviewMessageResponse,
  SendMessageRequest,
  SendMessageResponse,
  SimulatedNode,
  TransmitStatus,
} from "../api/types";

// Phase 5, THE HARD CONSTRAINT: nothing built here ever
// transmits to the real mesh. There is no config flag to permit it and
// deliberately no UI control for one — every send below is hard-coded
// transport:"simulated", and the composer states that in plain English at
// the point of sending (SIMULATED_SEND_NOTICE, lib/definitions.ts) rather
// than relying on a person reading a settings page first. GET
// /api/transmit/status is surfaced right above the composer for the same
// reason: the backend's own words on why real transmit isn't available.
//
// Judgement call, per the brief: simulated-node management lives HERE, as a
// side panel, not under Settings. Reasoning: a simulated node exists
// specifically to be a message-send target, and the entire point of this
// phase is the send path — needing to leave this page, create a target
// under an admin-only Settings screen, then come back to pick it in the
// composer breaks the one flow this page exists to demonstrate. Settings
// is for durable, admin-only server configuration; a simulated node is a
// disposable test fixture a viewer should be able to spin up and tear down
// without an admin account, which SettingsPage's isAdmin gate would
// otherwise block for no real reason.
//
// Layout: reuses .main-area/.side-pane exactly as LiveView does (rendered
// directly under .page-body, not .page-scroll, in App.tsx) — the same
// grid that was the subject of the earlier viewport-fill bug fix, so this
// page inherits that fix instead of risking a new one.

type TargetMode = "node" | "channel";

function endpointLabel(ep: MessageEndpoint | undefined | null): string {
  if (!ep) return "--";
  if (ep.channel) return `Channel: ${ep.channel}`;
  if (ep.native_id) {
    const proto = ep.protocol && isProtocol(ep.protocol) ? PROTOCOL_LABEL[ep.protocol] : ep.protocol;
    return `${ep.label || ep.native_id}${proto ? ` (${proto})` : ""}`;
  }
  return "--";
}

const SENT_PAGE = 100;

function SentTab({ timeWindow }: { timeWindow: MessageWindow }) {
  const { messages: liveMessages, upsertMessage, simulatedNodes, simulatedNodesAvailable, refreshSimulatedNodes, effectiveTheme } = useAppData();

  // --- time window: live messages (shared app list, kept current by the WebSocket) plus pages fetched for the
  // window from the server; shown newest first, only what falls inside the window.
  const [fetched, setFetched] = useState<MessageRecord[]>([]);
  const [olderFrom, setOlderFrom] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFetched([]);
    setOlderFrom(null);
    tryApiGet<{ messages: MessageRecord[] }>(`/api/messages?limit=${SENT_PAGE}${sinceQuery(timeWindow)}`)
      .then((r) => {
        if (cancelled || !r) return;
        setFetched(r.messages);
        setOlderFrom(r.messages.length >= SENT_PAGE ? r.messages[r.messages.length - 1].id : null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [timeWindow]);
  const loadOlder = useCallback(async () => {
    if (!olderFrom || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const r = await tryApiGet<{ messages: MessageRecord[] }>(
        `/api/messages?limit=${SENT_PAGE}&before=${encodeURIComponent(olderFrom)}${sinceQuery(timeWindow)}`,
      );
      if (r) {
        setFetched((prev) => [...prev, ...r.messages]);
        setOlderFrom(r.messages.length >= SENT_PAGE ? r.messages[r.messages.length - 1].id : null);
      }
    } catch {
      /* leave the button; the next click retries */
    } finally {
      setLoadingOlder(false);
    }
  }, [olderFrom, loadingOlder, timeWindow]);
  // Live copies come first so a state change from the WebSocket wins over the older fetched copy of the same id.
  const messages = windowRows([...liveMessages, ...fetched], (m) => m.id, (m) => m.created_at, timeWindow);

  // --- transmit status (prominent, informational only — never a control) --
  const [transmitStatus, setTransmitStatus] = useState<TransmitStatus | null | "unavailable">(null);
  useEffect(() => {
    let cancelled = false;
    tryApiGet<TransmitStatus>("/api/transmit/status")
      .then((r) => !cancelled && setTransmitStatus(r ?? "unavailable"))
      .catch(() => !cancelled && setTransmitStatus("unavailable"));
    return () => {
      cancelled = true;
    };
  }, []);

  // --- composer -------------------------------------------------------
  const [targetMode, setTargetMode] = useState<TargetMode>("node");
  const [targetNodeId, setTargetNodeId] = useState("");
  const [channelName, setChannelName] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [lastSendResult, setLastSendResult] = useState<SendMessageResponse | null>(null);

  // Resolves the composer's current selection into the wire "to" shape, or
  // null when there isn't a complete destination yet — shared by the
  // fragmentation preview below and onSend, so the two can never disagree
  // about what "the current destination" means.
  function buildTarget(): SendMessageRequest["to"] | null {
    if (targetMode === "channel") {
      const ch = channelName.trim();
      return ch ? { channel: ch } : null;
    }
    const n = simulatedNodes.find((s) => s.id === targetNodeId);
    return n ? { protocol: n.protocol, native_id: n.native_id } : null;
  }

  const canSend = text.trim().length > 0 && buildTarget() !== null && !sending;

  // --- fragmentation preview: the backend's real split, not a client-side
  // guess (closing the gap the old 200-byte estimate
  // deliberately flagged as an estimate). Debounced so it doesn't fire a
  // request per keystroke; never blocks Send if it fails or the endpoint
  // isn't live yet, since preview is informational, not load-bearing.
  const [preview, setPreview] = useState<PreviewMessageResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);

  useEffect(() => {
    const target = buildTarget();
    setPreview(null);
    setPreviewUnavailable(false);
    if (!target || text.trim().length === 0) {
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await apiPost<PreviewMessageResponse>("/api/messages/preview", { to: target, text });
        if (!cancelled) {
          setPreview(res);
          setPreviewLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPreviewUnavailable(true);
          setPreviewLoading(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMode, targetNodeId, channelName, text, simulatedNodes]);

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    const to = buildTarget();
    if (!canSend || !to) return;
    setSending(true);
    setSendError(null);
    setLastSendResult(null);
    try {
      const body: SendMessageRequest = { to, text, transport: "simulated" };
      const res = await apiPost<SendMessageResponse>("/api/messages/send", body);
      setLastSendResult(res);
      // Optimistic insert: POST /api/messages/send already tells us enough
      // (id, initial state, real fragment split) to show this message right
      // away rather than leaving the list stale until the first live WS
      // "message" event — that event, when it arrives, updates this exact
      // row in place (same id) rather than duplicating it.
      upsertMessage({
        id: res.message_id,
        direction: "out",
        to,
        from: {},
        text,
        transport: "simulated",
        fragments_total: res.fragments.length,
        fragments_acked: 0,
        state: res.state,
        state_history: [{ state: res.state, at: new Date().toISOString(), detail: null }],
        created_at: new Date().toISOString(),
      });
      setText("");
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Send failed.");
    } finally {
      setSending(false);
    }
  }

  // --- simulated node management (create/delete) -----------------------
  const [newLabel, setNewLabel] = useState("");
  const [newProtocol, setNewProtocol] = useState<"meshtastic" | "meshcore">("meshtastic");
  const [newRole, setNewRole] = useState("CLIENT");
  const [newLat, setNewLat] = useState("");
  const [newLon, setNewLon] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function onCreateSimNode(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    const hasPosition = newLat.trim() !== "" && newLon.trim() !== "";
    const position = hasPosition ? { lat: Number(newLat), lon: Number(newLon) } : null;
    try {
      const body: CreateSimulatedNodeRequest = { protocol: newProtocol, label: newLabel.trim() || "Sim node", role: newRole.trim() || "CLIENT", position };
      await apiPost<SimulatedNode>("/api/simulated/nodes", body);
      setNewLabel("");
      setNewLat("");
      setNewLon("");
      await refreshSimulatedNodes();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Couldn't create simulated node.");
    } finally {
      setCreating(false);
    }
  }

  async function onDeleteSimNode(id: string) {
    try {
      await apiDelete(`/api/simulated/nodes/${encodeURIComponent(id)}`);
      if (targetNodeId === id) setTargetNodeId("");
      await refreshSimulatedNodes();
    } catch {
      /* best-effort — the list simply still shows it if the delete failed */
    }
  }

  // --- message detail ---------------------------------------------------
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const selectedMessage: MessageRecord | null = messages.find((m) => m.id === selectedMessageId) ?? null;

  return (
    <div className="main-area">
      <div className="messages-main">
        <h2 className="audit-page-title">Messages</h2>

        <div className="messages-transmit-banner">
          <span className="badge" style={{ background: colorFor(TRUST_TIER_INFO.simulated.color, effectiveTheme) }}>
            Simulated only
          </span>
          {transmitStatus === null && "Checking transmit status…"}
          {transmitStatus === "unavailable" && "Transmit status unavailable right now."}
          {transmitStatus && transmitStatus !== "unavailable" && (
            <span>
              {liveTransmitDisabledNotice(transmitStatus.reason)} Allowed targets: {transmitStatus.allowed_targets.join(", ")}.
            </span>
          )}
        </div>

        <form className="messages-composer" onSubmit={onSend}>
          <div className="theme-toggle" role="group" aria-label="Destination type">
            <button type="button" data-active={targetMode === "node"} onClick={() => setTargetMode("node")}>
              Simulated node
            </button>
            <button type="button" data-active={targetMode === "channel"} onClick={() => setTargetMode("channel")}>
              Channel
            </button>
          </div>

          {targetMode === "node" ? (
            <select value={targetNodeId} onChange={(e) => setTargetNodeId(e.target.value)}>
              <option value="">Select a simulated node…</option>
              {simulatedNodes.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.label} ({isProtocol(n.protocol) ? PROTOCOL_LABEL[n.protocol] : n.protocol})
                </option>
              ))}
            </select>
          ) : (
            <input placeholder="Channel name, e.g. LongFast" value={channelName} onChange={(e) => setChannelName(e.target.value)} />
          )}

          <textarea rows={3} placeholder="Message text…" value={text} onChange={(e) => setText(e.target.value)} />

          <div className="messages-frag-preview">
            {text.trim().length === 0 || buildTarget() === null
              ? "Pick a destination and enter text to preview how it will be split."
              : previewLoading
                ? "Checking fragmentation…"
                : previewUnavailable
                  ? "Fragmentation preview isn't available from this backend right now — Send still works; you'll see the real split once it's queued."
                  : preview
                    ? `${preview.fragments.reduce((n, f) => n + f.bytes, 0)} byte(s) — ${preview.fragments.length} fragment(s), up to ${preview.budget_bytes} bytes each (${preview.protocol}).`
                    : ""}
          </div>

          <div className="node-detail-note">{SIMULATED_SEND_NOTICE}</div>

          {sendError && <div className="auth-error">{sendError}</div>}

          <button type="submit" className="settings-save-btn" disabled={!canSend}>
            {sending ? "Sending…" : "Send (simulated)"}
          </button>

          {lastSendResult && (
            <div className="messages-send-result">
              Queued as {lastSendResult.message_id} — the backend split it into {lastSendResult.fragments.length} fragment(s).
            </div>
          )}
        </form>

        <div className="messages-list-wrap">
          <table className="node-list-table">
            <thead>
              <tr>
                <th>State</th>
                <th>Dir</th>
                <th>To</th>
                <th>From</th>
                <th>Text</th>
                <th>Fragments</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => {
                const info = messageStateInfo(m.state);
                return (
                  <tr key={m.id} className="node-list-row" data-active={m.id === selectedMessageId} onClick={() => setSelectedMessageId(m.id)}>
                    <td>
                      <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
                        {info.label}
                      </span>
                    </td>
                    <td>{m.direction}</td>
                    <td>{endpointLabel(m.to)}</td>
                    <td>{endpointLabel(m.from)}</td>
                    <td>{m.text}</td>
                    <td>
                      {m.fragments_acked}/{m.fragments_total}
                    </td>
                    <td>{relativeTime(m.created_at)}</td>
                  </tr>
                );
              })}
              {messages.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">
                    {timeWindow === "all" ? "No messages yet." : "No messages in this time range."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {olderFrom && (
            <button type="button" className="rf-link-btn" disabled={loadingOlder} onClick={() => void loadOlder()}>
              {loadingOlder ? "Loading..." : "Show older"}
            </button>
          )}
        </div>
      </div>

      <div className="side-pane">
        <div className="panel-block">
          <div className="panel-header">
            <span>Simulated nodes</span>
            <span style={{ fontWeight: 400, textTransform: "none" }}>{simulatedNodes.length}</span>
          </div>

          <form className="messages-simnode-form" onSubmit={onCreateSimNode}>
            <div className="theme-toggle" role="group" aria-label="Simulated node protocol">
              <button type="button" data-active={newProtocol === "meshtastic"} onClick={() => setNewProtocol("meshtastic")}>
                Meshtastic
              </button>
              <button type="button" data-active={newProtocol === "meshcore"} onClick={() => setNewProtocol("meshcore")}>
                MeshCore
              </button>
            </div>
            <input placeholder="Label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            <input placeholder="Role, e.g. CLIENT" value={newRole} onChange={(e) => setNewRole(e.target.value)} />
            <div style={{ display: "flex", gap: 6 }}>
              <input placeholder="lat (optional)" value={newLat} onChange={(e) => setNewLat(e.target.value)} />
              <input placeholder="lon (optional)" value={newLon} onChange={(e) => setNewLon(e.target.value)} />
            </div>
            {createError && <div className="auth-error">{createError}</div>}
            <button type="submit" className="settings-save-btn" disabled={creating}>
              {creating ? "Adding…" : "Add simulated node"}
            </button>
          </form>

          {!simulatedNodesAvailable && simulatedNodes.length === 0 ? (
            <div className="node-detail-note">Simulated nodes aren't available on this build yet.</div>
          ) : simulatedNodes.length === 0 ? (
            <div className="empty-state">No simulated nodes yet — add one above to have a destination to send to.</div>
          ) : (
            <ul className="messages-simnode-list">
              {simulatedNodes.map((n) => (
                <li key={n.id}>
                  <span className="badge" style={{ background: colorFor(TRUST_TIER_INFO.simulated.color, effectiveTheme) }}>
                    Sim
                  </span>
                  <span>
                    {n.label} <span className="audit-subject-protocol">({isProtocol(n.protocol) ? PROTOCOL_LABEL[n.protocol] : n.protocol})</span>
                  </span>
                  <button className="close-btn" title="Delete this simulated node" onClick={() => onDeleteSimNode(n.id)}>
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="panel-block" style={{ flex: 1 }}>
          <div className="panel-header">
            <span>Message detail</span>
            {selectedMessage && (
              <button className="close-btn" onClick={() => setSelectedMessageId(null)}>
                ✕
              </button>
            )}
          </div>
          {!selectedMessage ? (
            <div className="empty-state">Select a message to see its full delivery history.</div>
          ) : (
            <div style={{ padding: "0 12px 12px" }}>
              <div className="node-detail-row">
                <span className="k">State</span>
                <span className="badge" style={{ background: colorFor(messageStateInfo(selectedMessage.state).color, effectiveTheme) }}>
                  {messageStateInfo(selectedMessage.state).label}
                </span>
              </div>
              <div className="node-detail-row">
                <span className="k">To</span>
                <span>{endpointLabel(selectedMessage.to)}</span>
              </div>
              <div className="node-detail-row">
                <span className="k">From</span>
                <span>{endpointLabel(selectedMessage.from)}</span>
              </div>
              <div className="node-detail-row">
                <span className="k">Fragments</span>
                <span>
                  {selectedMessage.fragments_acked}/{selectedMessage.fragments_total}
                </span>
              </div>
              <div className="node-detail-row">
                <span className="k">Text</span>
                <span>{selectedMessage.text}</span>
              </div>

              <h3 className="messages-history-title">Delivery history</h3>
              <ul className="messages-state-history">
                {selectedMessage.state_history.map((h, i) => {
                  const info = messageStateInfo(h.state);
                  return (
                    <li key={i}>
                      <span className="badge" style={{ background: colorFor(info.color, effectiveTheme) }}>
                        {info.label}
                      </span>
                      <span>{clockTime(h.at)}</span>
                      {h.detail && <span className="messages-state-detail">{h.detail}</span>}
                    </li>
                  );
                })}
                {selectedMessage.state_history.length === 0 && <li className="messages-state-detail">No history recorded.</li>}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type MessagesTab = "channels" | "sent";

// Two tabs: Channels (read-only MeshCore channel messages we hear) and Sent
// (the original send box + log, unchanged).
export function MessagesPage() {
  const [tab, setTab] = useState<MessagesTab>("channels");
  const [timeWindow, setTimeWindowState] = useState<MessageWindow>(() => {
    try {
      return parseMessageWindow(localStorage.getItem(MESSAGE_WINDOW_STORAGE_KEY));
    } catch {
      return parseMessageWindow(null); // storage blocked: default, just don't persist
    }
  });
  const setTimeWindow = (w: MessageWindow) => {
    setTimeWindowState(w);
    try {
      localStorage.setItem(MESSAGE_WINDOW_STORAGE_KEY, w);
    } catch {
      /* same as above */
    }
  };
  return (
    <div className="messages-tabs-wrap">
      <div className="settings-tabs messages-top-tabs" role="tablist">
        <button type="button" role="tab" className="settings-tab" data-active={tab === "channels"} onClick={() => setTab("channels")}>
          Channels
        </button>
        <button type="button" role="tab" className="settings-tab" data-active={tab === "sent"} onClick={() => setTab("sent")}>
          Sent
        </button>
        <label className="messages-time-filter">
          <span>Show</span>
          <select aria-label="Time range" value={timeWindow} onChange={(e) => setTimeWindow(parseMessageWindow(e.target.value))}>
            {MESSAGE_WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="messages-tabs-body">
        {tab === "sent" ? <SentTab timeWindow={timeWindow} /> : <ChannelsTab timeWindow={timeWindow} />}
      </div>
    </div>
  );
}
